import { useState, type AnchorHTMLAttributes, type ReactNode } from 'react'
import { Copy, Check, ExternalLink, Link } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Long OAuth / query-string URLs render as a compact chip instead of clipping under overflow-x-hidden. */
const LONG_URL_CHAR_THRESHOLD = 80

function getUrlDisplayLabel(href: string, children?: ReactNode): string {
  const text = String(children ?? href).trim()
  if (text && text !== href) return text
  try {
    const u = new URL(href)
    return u.hostname + (u.pathname !== '/' ? u.pathname : '')
  } catch {
    return href
  }
}

export function AssistantExternalLink({
  href,
  children,
  className,
  ...domProps
}: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const isLong = href.length > LONG_URL_CHAR_THRESHOLD
  const [copied, setCopied] = useState(false)

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    void navigator.clipboard.writeText(href).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    })
  }

  if (isLong) {
    const display = getUrlDisplayLabel(href, children)
    return (
      <span className="inline-flex flex-wrap items-center gap-1 max-w-full align-middle">
        <a
          href={href}
          className={cn(
            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs border transition-colors',
            'bg-background/60 text-primary border-border hover:bg-muted hover:underline',
            'no-underline max-w-full min-w-0',
            className,
          )}
          {...domProps}
          target="_blank"
          rel="noopener noreferrer"
          title={href}
        >
          <Link className="w-3 h-3 shrink-0" aria-hidden />
          <span className="truncate min-w-0">{display}</span>
          <ExternalLink className="w-3 h-3 shrink-0 opacity-60" aria-hidden />
        </a>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-border bg-background/60 text-muted-foreground hover:bg-muted hover:text-foreground shrink-0"
          title="Copy link"
          aria-label="Copy link"
        >
          {copied ? (
            <Check className="w-3 h-3 text-green-500" aria-hidden />
          ) : (
            <Copy className="w-3 h-3" aria-hidden />
          )}
        </button>
      </span>
    )
  }

  return (
    <a
      href={href}
      className={cn(
        'text-primary hover:underline inline break-all [overflow-wrap:anywhere]',
        className,
      )}
      {...domProps}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
      <ExternalLink className="inline h-3 w-3 ml-0.5 opacity-60 align-text-bottom" aria-hidden />
    </a>
  )
}
